import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { Link } from 'react-router-dom';

export default function TankLeasing() {
  const features = [
    {
      icon: 'ri-building-line',
      title: '100,000 m³ Capacity',
      description: 'Modern tank farm with substantial storage capacity to meet diverse client needs.',
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Advanced Safety Features',
      description: 'State-of-the-art safety systems including fire suppression, leak detection, and emergency protocols.',
    },
    {
      icon: 'ri-line-chart-line',
      title: 'Commercial Accounting',
      description: 'Sophisticated accounting system for accurate inventory tracking and billing.',
    },
    {
      icon: 'ri-eye-line',
      title: 'Real-Time Monitoring',
      description: 'Remote access to critical data including volume, level, density, and available capacity.',
    },
    {
      icon: 'ri-truck-line',
      title: 'Receipt & Delivery',
      description: 'Comprehensive service including product receipt and delivery coordination.',
    },
    {
      icon: 'ri-calendar-check-line',
      title: 'Flexible Terms',
      description: 'Customizable leasing agreements to match your operational requirements.',
    },
  ];

  const benefits = [
    'No capital investment required',
    'Scalable storage capacity',
    'Professional facility management',
    'Reduced operational overhead',
    'Access to strategic location',
    'Compliance with all regulations',
    'Integrated logistics services',
    'Transparent cost structure',
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-grow">
        {/* Hero Section */}
        <section 
          className="relative min-h-[500px] flex items-center"
          style={{
            backgroundImage: `linear-gradient(rgba(0, 20, 60, 0.7), rgba(0, 20, 60, 0.7)), url('https://public.readdy.ai/ai/img_res/5fc8cbd0af6dc1d3b031be28bc749225.jpg')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        >
          <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
            <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6">Tank Leasing Services</h1>
            <p className="text-xl text-gray-200 max-w-3xl leading-relaxed">
              Flexible tank leasing options with 100,000 m³ total storage capacity and advanced real-time monitoring systems
            </p>
          </div>
        </section>

        {/* Overview Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              <div className="relative rounded-xl overflow-hidden shadow-lg">
                <img 
                  src="https://readdy.ai/api/search-image?query=Real%20petroleum%20storage%20tanks%20available%20for%20leasing%20at%20working%20tank%20farm%20facility%20showing%20modern%20infrastructure%20and%20monitoring%20systems%20under%20natural%20daylight%2C%20authentic%20documentary%20photography%20of%20actual%20industrial%20tank%20leasing%20operations%20with%20professional%20safety%20equipment%20and%20advanced%20technology%2C%20professional%20photojournalism%20of%20genuine%20operational%20oil%20storage%20tanks%20with%20real-time%20monitoring%2C%20realistic%20commercial%20photograph&width=600&height=400&seq=leasing-facility-001&orientation=landscape"
                  alt="Tank Leasing Facility"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="relative rounded-xl overflow-hidden shadow-lg">
                <img 
                  src="https://readdy.ai/api/search-image?query=Real%20petroleum%20storage%20tank%20monitoring%20control%20room%20with%20actual%20digital%20displays%20and%20real-time%20monitoring%20systems%2C%20authentic%20documentary%20photography%20of%20genuine%20industrial%20tank%20leasing%20operations%20showing%20professional%20monitoring%20technology%20and%20safety%20protocols%2C%20professional%20photojournalism%20of%20actual%20control%20center%20for%20oil%20storage%20tanks%20with%20modern%20equipment%2C%20realistic%20commercial%20photograph%20of%20monitoring%20facility&width=600&height=400&seq=leasing-monitoring-001&orientation=landscape"
                  alt="Monitoring Systems"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
              <div className="relative">
                <img 
                  src="https://readdy.ai/api/search-image?query=Real%20petroleum%20storage%20tanks%20available%20for%20leasing%20at%20professional%20facility%2C%20authentic%20documentary%20photography%20of%20actual%20tank%20leasing%20operations%20with%20modern%20monitoring%20systems%2C%20professional%20photojournalism%20showing%20genuine%20industrial%20storage%20tanks%20with%20real%20infrastructure%20and%20safety%20equipment%2C%20realistic%20commercial%20photograph%20of%20working%20tank%20farm%20leasing%20facility&width=600&height=400&seq=leasing-facility-001&orientation=landscape"
                  alt="Tank Leasing Facility"
                  className="rounded-2xl shadow-2xl w-full h-auto object-cover object-top"
                />
              </div>
              <div>
                <h2 className="text-4xl font-bold text-gray-900 mb-6">Modern Tank Farm Leasing</h2>
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                  We offer flexible tank leasing options within our modern tank farm, which boasts a total storage capacity of 100,000 m³. Our comprehensive service includes product receipt and delivery, providing you with a complete storage solution without the capital investment of building your own facility.
                </p>
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                  For safety and transparency, each tank is outfitted with advanced safety features and a commercial accounting system. This system enables remote, real-time monitoring of critical data, including product volume, level, density, and available capacity.
                </p>
                <p className="text-lg text-gray-600 leading-relaxed">
                  Our tank leasing service provides the flexibility and scalability you need to manage your petroleum storage requirements efficiently, with the peace of mind that comes from professional facility management and comprehensive safety systems.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Leasing Features</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Comprehensive tank leasing solutions with advanced technology and professional management
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <div key={index} className="bg-white rounded-xl p-8 shadow-sm hover:shadow-lg transition-shadow">
                  <div className="w-14 h-14 bg-blue-100 rounded-lg flex items-center justify-center mb-6">
                    <i className={`${feature.icon} text-2xl text-blue-600`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div>
                <h2 className="text-4xl font-bold text-gray-900 mb-6">Benefits of Tank Leasing</h2>
                <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                  Tank leasing offers numerous advantages over building and maintaining your own storage facility. Enjoy professional management, reduced costs, and operational flexibility.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {benefits.map((benefit, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <i className="ri-check-line text-blue-600 text-xl mt-1"></i>
                      <span className="text-gray-700">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-gray-50 rounded-2xl p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Monitoring Capabilities</h3>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-dashboard-line text-xl text-blue-600"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-2">Product Volume</h4>
                      <p className="text-gray-600">Real-time tracking of stored product quantities</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-bar-chart-line text-xl text-blue-600"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-2">Tank Level</h4>
                      <p className="text-gray-600">Continuous monitoring of fill levels</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-water-percent-line text-xl text-blue-600"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-2">Product Density</h4>
                      <p className="text-gray-600">Accurate density measurements for quality control</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-database-line text-xl text-blue-600"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-2">Available Capacity</h4>
                      <p className="text-gray-600">Instant visibility of remaining storage space</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-blue-600">
          <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
            <h2 className="text-4xl font-bold text-white mb-6">Inquire About Tank Leasing</h2>
            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
              Contact us to discuss available capacity and leasing terms for your storage needs
            </p>
            <Link 
              to="/contact" 
              className="inline-block px-8 py-4 bg-white text-blue-600 font-semibold rounded-lg hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              Contact Us
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}